import React, { useState, useEffect } from 'react';
import { 
  Save, Mail, Send, CheckCircle, AlertTriangle, 
  Settings as SettingsIcon, Database, Globe, Shield,
  Eye, EyeOff, RefreshCw, TestTube, Zap
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useEmail } from '../../hooks/useEmail';

const Settings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showTokens, setShowTokens] = useState(false);
  
  const { sendNotification } = useEmail();
  
  const [zeptomailConfig, setZeptomailConfig] = useState({
    apiUrl: 'https://api.zeptomail.com/v1.1/email',
    fromEmail: '',
    fromName: 'Made in BD',
    verificationToken: '',
    orderConfirmationToken: '',
    passwordResetToken: '',
    notificationToken: ''
  });
  
  const [testEmail, setTestEmail] = useState({
    to: '',
    subject: 'Test Email from Made in BD',
    message: 'This is a test email to verify ZeptoMail configuration is working correctly.'
  });

  // Load ZeptoMail configuration on component mount
  useEffect(() => {
    loadZeptomailConfig();
  }, []);

  const loadZeptomailConfig = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_key', 'zeptomail_config')
        .maybeSingle();
      
      if (error) {
        console.error('Error loading ZeptoMail config:', error);
        return;
      }
      
      if (data && data.setting_value) {
        setZeptomailConfig(data.setting_value);
      }
    } catch (err) {
      console.error('Error loading ZeptoMail config:', err);
      setError('Failed to load email configuration');
    } finally {
      setLoading(false);
    }
  };

  const saveZeptomailConfig = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      
      // Validate required fields
      if (!zeptomailConfig.fromEmail || !zeptomailConfig.verificationToken) {
        setError('From email and verification token are required');
        return;
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(zeptomailConfig.fromEmail)) {
        setError('Please enter a valid email address');
        return;
      }
      
      const { error } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'zeptomail_config',
          setting_value: zeptomailConfig,
          setting_type: 'email',
          description: 'ZeptoMail email service configuration'
        }, {
          onConflict: 'setting_key'
        });
      
      if (error) throw error;
      
      setSuccess('ZeptoMail configuration saved successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
      
    } catch (err) {
      console.error('Error saving ZeptoMail config:', err);
      setError('Failed to save configuration. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const testZeptomailConfig = async () => {
    try {
      setTestingEmail(true);
      setError(null);
      setSuccess(null);
      
      if (!testEmail.to) {
        setError('Please enter a test email address');
        return;
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(testEmail.to)) {
        setError('Please enter a valid test email address');
        return;
      }
      
      // Send test email
      const result = await sendNotification(
        testEmail.to,
        'Test User',
        testEmail.subject,
        testEmail.message
      );
      
      if (result) {
        setSuccess(`Test email sent successfully to ${testEmail.to}`);
      } else {
        setError('Failed to send test email. Please check your configuration.');
      }
      
    } catch (err) {
      console.error('Error testing email:', err);
      setError('Failed to send test email. Please check your configuration.');
    } finally {
      setTestingEmail(false);
    }
  };

  const handleConfigChange = (field: string, value: string) => {
    setZeptomailConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTestEmailChange = (field: string, value: string) => {
    setTestEmail(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">System Settings</h1>
              <p className="text-gray-600">Configure email services and system preferences</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center">
            <CheckCircle className="h-5 w-5 mr-2" />
            <span>{success}</span>
          </div>
        )}
        
        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center">
            <AlertTriangle className="h-5 w-5 mr-2" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* ZeptoMail Configuration */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-6">
                <div className="p-2 bg-blue-100 rounded-lg mr-3">
                  <Mail className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">ZeptoMail Configuration</h2>
                  <p className="text-gray-600">Configure email service for notifications and confirmations</p>
                </div>
              </div>
              
              <div className="space-y-6">
                {/* API URL */}
                <div>
                  <label htmlFor="apiUrl" className="block text-sm font-medium text-gray-700 mb-2">
                    API URL
                  </label>
                  <div className="relative">
                    <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="apiUrl"
                      type="url"
                      value={zeptomailConfig.apiUrl}
                      onChange={(e) => handleConfigChange('apiUrl', e.target.value)}
                      className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="https://api.zeptomail.com/v1.1/email"
                    />
                  </div>
                </div>
                
                {/* From Email */}
                <div>
                  <label htmlFor="fromEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    From Email Address *
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      id="fromEmail"
                      type="email"
                      required
                      value={zeptomailConfig.fromEmail}
                      onChange={(e) => handleConfigChange('fromEmail', e.target.value)}
                      className="pl-10 w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                      placeholder="noreply@madeinbd.com"
                    />
                  </div>
                </div>
                
                {/* From Name */}
                <div>
                  <label htmlFor="fromName" className="block text-sm font-medium text-gray-700 mb-2">
                    From Name
                  </label>
                  <input
                    id="fromName"
                    type="text"
                    value={zeptomailConfig.fromName}
                    onChange={(e) => handleConfigChange('fromName', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Made in BD"
                  />
                </div>
                
                {/* API Tokens */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-md font-medium text-gray-900">API Tokens</h3>
                    <button
                      type="button"
                      onClick={() => setShowTokens(!showTokens)}
                      className="text-sm text-gray-600 hover:text-gray-800 flex items-center space-x-1"
                    >
                      {showTokens ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      <span>{showTokens ? 'Hide' : 'Show'} Tokens</span>
                    </button>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="verificationToken" className="block text-sm font-medium text-gray-700 mb-2">
                        Verification Token *
                      </label>
                      <input
                        id="verificationToken"
                        type={showTokens ? 'text' : 'password'}
                        required
                        value={zeptomailConfig.verificationToken}
                        onChange={(e) => handleConfigChange('verificationToken', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                        placeholder="Enter verification token"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="orderConfirmationToken" className="block text-sm font-medium text-gray-700 mb-2">
                        Order Confirmation Token
                      </label>
                      <input
                        id="orderConfirmationToken"
                        type={showTokens ? 'text' : 'password'}
                        value={zeptomailConfig.orderConfirmationToken}
                        onChange={(e) => handleConfigChange('orderConfirmationToken', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                        placeholder="Enter order confirmation token"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="passwordResetToken" className="block text-sm font-medium text-gray-700 mb-2">
                        Password Reset Token
                      </label>
                      <input
                        id="passwordResetToken"
                        type={showTokens ? 'text' : 'password'}
                        value={zeptomailConfig.passwordResetToken}
                        onChange={(e) => handleConfigChange('passwordResetToken', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                        placeholder="Enter password reset token"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="notificationToken" className="block text-sm font-medium text-gray-700 mb-2">
                        Notification Token
                      </label>
                      <input
                        id="notificationToken"
                        type={showTokens ? 'text' : 'password'}
                        value={zeptomailConfig.notificationToken}
                        onChange={(e) => handleConfigChange('notificationToken', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent font-mono text-sm"
                        placeholder="Enter notification token"
                      />
                    </div>
                  </div>
                </div>
                
                {/* Save Configuration */}
                <div className="flex justify-end">
                  <button
                    onClick={saveZeptomailConfig}
                    disabled={saving}
                    className="bg-orange-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4" />
                        <span>Save Configuration</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Test Email & Status */}
          <div className="space-y-6">
            {/* Configuration Status */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-green-100 rounded-lg mr-3">
                  <Shield className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Configuration Status</h3>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">API URL</span>
                  <div className="flex items-center">
                    {zeptomailConfig.apiUrl ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}
                    <span className={`ml-2 text-sm ${zeptomailConfig.apiUrl ? 'text-green-600' : 'text-red-600'}`}>
                      {zeptomailConfig.apiUrl ? 'Configured' : 'Missing'}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">From Email</span>
                  <div className="flex items-center">
                    {zeptomailConfig.fromEmail ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}
                    <span className={`ml-2 text-sm ${zeptomailConfig.fromEmail ? 'text-green-600' : 'text-red-600'}`}>
                      {zeptomailConfig.fromEmail ? 'Configured' : 'Missing'}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Verification Token</span>
                  <div className="flex items-center">
                    {zeptomailConfig.verificationToken ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                    )}
                    <span className={`ml-2 text-sm ${zeptomailConfig.verificationToken ? 'text-green-600' : 'text-red-600'}`}>
                      {zeptomailConfig.verificationToken ? 'Configured' : 'Missing'}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Other Tokens</span>
                  <div className="flex items-center">
                    {(zeptomailConfig.orderConfirmationToken || zeptomailConfig.passwordResetToken || zeptomailConfig.notificationToken) ? (
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    )}
                    <span className={`ml-2 text-sm ${(zeptomailConfig.orderConfirmationToken || zeptomailConfig.passwordResetToken || zeptomailConfig.notificationToken) ? 'text-green-600' : 'text-yellow-600'}`}>
                      {(zeptomailConfig.orderConfirmationToken || zeptomailConfig.passwordResetToken || zeptomailConfig.notificationToken) ? 'Partial' : 'Optional'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <button
                  onClick={loadZeptomailConfig}
                  className="w-full bg-gray-100 text-gray-700 py-2 rounded-lg font-medium hover:bg-gray-200 transition-colors flex items-center justify-center space-x-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  <span>Reload Configuration</span>
                </button>
              </div>
            </div>
            
            {/* Test Email */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-purple-100 rounded-lg mr-3">
                  <TestTube className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Test Email</h3>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="testEmailTo" className="block text-sm font-medium text-gray-700 mb-2">
                    Test Email Address *
                  </label>
                  <input
                    id="testEmailTo"
                    type="email"
                    required
                    value={testEmail.to}
                    onChange={(e) => handleTestEmailChange('to', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="test@example.com"
                  />
                </div>
                
                <div>
                  <label htmlFor="testEmailSubject" className="block text-sm font-medium text-gray-700 mb-2">
                    Subject
                  </label>
                  <input
                    id="testEmailSubject"
                    type="text"
                    value={testEmail.subject}
                    onChange={(e) => handleTestEmailChange('subject', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Test Email Subject"
                  />
                </div>
                
                <div>
                  <label htmlFor="testEmailMessage" className="block text-sm font-medium text-gray-700 mb-2">
                    Message
                  </label>
                  <textarea
                    id="testEmailMessage"
                    rows={3}
                    value={testEmail.message}
                    onChange={(e) => handleTestEmailChange('message', e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Test email message"
                  />
                </div>
                
                <button
                  onClick={testZeptomailConfig}
                  disabled={testingEmail || !zeptomailConfig.fromEmail || !zeptomailConfig.verificationToken}
                  className="w-full bg-purple-600 text-white py-2 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                >
                  {testingEmail ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Sending Test Email...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span>Send Test Email</span>
                    </>
                  )}
                </button>
              </div>
            </div>
            
            {/* Setup Instructions */}
            <div className="bg-blue-50 rounded-lg p-6">
              <div className="flex items-center mb-4">
                <div className="p-2 bg-blue-100 rounded-lg mr-3">
                  <Zap className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Setup Instructions</h3>
              </div>
              
              <div className="space-y-3 text-sm text-gray-700">
                <div className="flex items-start space-x-2">
                  <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">1</span>
                  <div>
                    <p className="font-medium">Create ZeptoMail Account</p>
                    <p className="text-gray-600">Sign up at <a href="https://www.zoho.com/zeptomail/" target="_blank" className="text-blue-600 hover:text-blue-700">zeptomail.com</a></p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-2">
                  <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">2</span>
                  <div>
                    <p className="font-medium">Verify Your Domain</p>
                    <p className="text-gray-600">Add and verify your sending domain in ZeptoMail</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-2">
                  <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">3</span>
                  <div>
                    <p className="font-medium">Generate API Tokens</p>
                    <p className="text-gray-600">Create separate tokens for different email types in ZeptoMail dashboard</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-2">
                  <span className="bg-blue-100 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 mt-0.5">4</span>
                  <div>
                    <p className="font-medium">Configure & Test</p>
                    <p className="text-gray-600">Enter your tokens above and send a test email to verify functionality</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;